import { z, ZodType } from 'zod';
import { successOpenApiDescription } from '../constants/schemas.constants';
import { type TranslationKeys } from '../types/locale';

export type BaseSchema = {
	name: string;
	schema: ZodType;
};

export const requiredString = (label: TranslationKeys) =>
	z
		.string({ error: `${label}:zodErrors.isRequired` })
		.trim()
		.nonempty(`zodErrors.cannotBeEmpty:${label}`);

export const requiredEmail = (label: TranslationKeys) =>
	z
		.email({ error: `${label}:zodErrors.invalidEmailAddress` })
		.trim()
		.nonempty(`zodErrors.cannotBeEmpty:${label}`);

export const requiredUuid = (label: TranslationKeys) =>
	z
		.uuid({ error: `${label}:zodErrors.invalidUuidFormat` })
		.trim()
		.nonempty(`zodErrors.cannotBeEmpty:${label}`);

export const requiredNumber = (label: TranslationKeys) =>
	z.number({ error: `${label}:zodErrors.isRequired` });

export const requiredBoolean = (label: TranslationKeys) =>
	z.boolean({ error: `${label}:zodErrors.isRequired` });

export const requiredArray = <T extends z.ZodTypeAny>(
	schema: T,
	label: TranslationKeys
) =>
	z
		.array(schema, { error: `${label}:isRequired` })
		.nonempty(`${label}:zodErrors.cannotBeEmpty`);

export const onlySuccessSchema = z.object({
	success: z.boolean().default(true).meta(successOpenApiDescription),
});

export const errorSchema = z
	.object({
		success: z.boolean().default(false),
		name: requiredString('schemaAttributes.errorName').meta({
			description: 'Name of the error.',
			example: 'ValidationError',
		}),
		message: requiredString('schemaAttributes.errorMessage').meta({
			description: 'Human-readable explanation of the error.',
			example: 'error request',
		}),
		details: z
			.record(z.string(), z.array(z.string()))
			.optional()
			.meta({
				description:
					'Optional object containing field-specific error messages.',
				example: {
					email: [
						'Email is required',
						'Email must be a valid email address',
					],
				},
			}),
		stack: z.string().optional().meta({
			description:
				'Stack trace of the error, only in non-production environments.',
			example:
				'Error: Invalid input\n    at Object.<anonymous> (index.js:5:9)',
		}),
	})
	.strip();
